import { getOtColorSettings, otColorFor } from '@/utils/otColors';
import { applyOfficerOrder } from '@/utils/officerOrder';

/**
 * Frontend mirror of backend/config.py MODE_CONFIG.
 *
 * Keep in sync with:
 *   - backend/config.py  (MODE_CONFIG, NUM_SLOTS)
 *   - backend/time_utils.py  (hhmm_to_slot, hhmm_to_end_slot)
 *
 * hhmm_to_slot / hhmm_to_end_slot cannot be imported directly (Python-only),
 * so the equivalent logic is re-implemented here in TypeScript.
 */

/**
 * Fixed shift geometry — mirrors the constants of the same name in
 * backend/config.py. Every shift is 48 slots of 15 minutes, starting at 1000
 * (morning) or 2200 (night).
 *
 * These are NOT admin settings. Both languages hardcode them in slot arithmetic
 * and grid rendering, so an "editable" value could only ever disagree with how
 * the roster is actually built.
 */
export const NUM_SLOTS = 48;
export const SLOT_DURATION_MINUTES = 15;
export const MORNING_START_HOUR = 10;
export const NIGHT_START_HOUR = 22;

// ─── Duration colour palette ─────────────────────────────────────────────────

/**
 * Six-step duration colour palette shared by all pill components.
 * Index 0 = no duration / shortest step; index 5 = longest (clamped).
 *
 * Usage:
 *   const step = getDurationPalette(minutes, stepMins);
 *   // e.g. break pills: stepMins = 15
 *   // e.g. HW counter pills in GenerateWizard: stepMins = 30
 */
const DURATION_PALETTE = [
  { bg: '#FFFFFF', textColor: '#374151', border: '1px solid #d1d5db' }, // step 0
  { bg: '#DCEBFF', textColor: '#1e3a5f', border: 'none' },              // step 1
  { bg: '#AFCFFF', textColor: '#1e3a5f', border: 'none' },              // step 2
  { bg: '#5A9BFF', textColor: 'white',   border: 'none' },              // step 3
  { bg: '#1F4ED8', textColor: 'white',   border: 'none' },              // step 4
  { bg: '#6B21A8', textColor: 'white',   border: 'none' },              // step 5+
] as const;

export type DurationPaletteEntry = (typeof DURATION_PALETTE)[number];

/**
 * Map a duration in minutes to a palette entry.
 * @param durationMins  duration in minutes
 * @param stepMins      size of each colour step in minutes (e.g. 15 or 30)
 */
export function getDurationPalette(durationMins: number, stepMins: number): DurationPaletteEntry {
  const idx = Math.min(
    Math.floor(durationMins / stepMins),
    DURATION_PALETTE.length - 1,
  );
  return DURATION_PALETTE[idx];
}

// ─── Priority (rank) colour palette ────────────────────────────────────────

/**
 * Six-step sequential indigo palette for showing ORDER/PRIORITY (not
 * duration) — e.g. Settings > Priority Counters' zone and counter rank.
 * Deliberately a single hue (indigo, matching the app's existing accent
 * colour) so intensity alone reads as "how important", without borrowing
 * DURATION_PALETTE's blue→purple ramp, which already means something else
 * (time length) elsewhere in the app.
 */
const PRIORITY_PALETTE = [
  { bg: '#FFFFFF', textColor: '#6b7280', border: '1px solid #e5e7eb' }, // step 0 — lowest priority
  { bg: '#EEF2FF', textColor: '#4338ca', border: 'none' },
  { bg: '#C7D2FE', textColor: '#3730a3', border: 'none' },
  { bg: '#818CF8', textColor: '#FFFFFF', border: 'none' },
  { bg: '#4F46E5', textColor: '#FFFFFF', border: 'none' },
  { bg: '#312E81', textColor: '#FFFFFF', border: 'none' },              // step 5 — highest priority
] as const;

export type PriorityPaletteEntry = (typeof PRIORITY_PALETTE)[number];

/**
 * Map a 1-based rank within a list of `total` items to a PRIORITY_PALETTE
 * step — most saturated/darkest at rank 1, fading to plain white/gray at
 * the last rank — so relative priority reads at a glance from colour alone,
 * without needing a "highest" label on every row.
 */
export function getPriorityShade(rank: number, total: number): PriorityPaletteEntry {
  const steps = PRIORITY_PALETTE.length;
  const t = total <= 1 ? 0 : (rank - 1) / (total - 1);
  const idx = Math.round(t * (steps - 1));
  return PRIORITY_PALETTE[steps - 1 - idx];
}

// ─── Break duration options ──────────────────────────────────────────────────

/**
 * Break durations offered across every break dropdown (officer edit, add
 * officers, auto-assign modal, counter grid), in 15-min slots.
 * Single source of truth — add a new duration here and it appears everywhere.
 */
const BREAK_DURATION_SLOTS = [1, 2, 3, 4, 6, 8, 10] as const;

export interface BreakOption {
  slots: number;
  label: string;
}

/**
 * Format a duration in minutes using the app-wide time convention: hours are
 * always "h", minutes are always "min" (never "hr" or a bare "m") — e.g.
 * "15min", "45min", "1h", "1h30min", "2h30min". Single source of truth for
 * every break duration label in the app (dropdowns, grid pills, badges).
 */
export function formatBreakMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m}min`;
}

/** Format a break duration given in 15-min slots (see formatBreakMinutes). */
function formatBreakDuration(slots: number): string {
  return formatBreakMinutes(slots * 15);
}

/** The break-duration options shown in a break dropdown. */
export function getBreakOptions(): BreakOption[] {
  return BREAK_DURATION_SLOTS.map((slots) => ({ slots, label: formatBreakDuration(slots) }));
}

/**
 * Officer ID prefixes — single source of truth for the frontend.
 * Must stay in sync with backend/officer.py:
 *   prefix = {"MAIN": "SN", "SOS": "S", "OT": "OT"}[self.officer_type]
 */
const OFFICER_PREFIXES = {
  MAIN: 'SN',
  SOS: 'S',
  OT: 'OT',
} as const;

/** Derive officer type from an officer key string (e.g. "SN1", "S3", "OT2"). */
export function getOfficerType(officerKey: string): 'main' | 'sos' | 'ot' | 'unknown' {
  // Check OT before SOS so "OT" isn't accidentally matched by a shorter prefix
  if (officerKey.startsWith(OFFICER_PREFIXES.OT)) return 'ot';
  if (officerKey.startsWith(OFFICER_PREFIXES.MAIN)) return 'main';
  if (officerKey.startsWith(OFFICER_PREFIXES.SOS)) return 'sos';
  return 'unknown';
}

/** Returns the fixed start hour for a given shift type (morning=10, night=22). */
export function getStartHour(shiftType: string): number {
  return shiftType === 'night' ? NIGHT_START_HOUR : MORNING_START_HOUR;
}

export interface ModeConfig {
  /** Total number of counters for this mode (car counters + motor counters) */
  numCounters: number;
  /**
   * Zone definitions as [start, end) index pairs (0-based, exclusive end).
   * Zone 1..4 are the car counter zones; motor counters are after zone4.
   * zoneBoundaryRows is derived from these: each zone's exclusive end index.
   * e.g. arrival zones [[0,10],[10,20],[20,30],[30,40]] →
   *   zoneBoundaryRows = [10, 20, 30, 40]
   *   → separator lines at y=9.5, 19.5, 29.5, 39.5 (after zone4's last car counter)
   */
  zones: [number, number][];
  /** Derived from zones — exclusive end index of each zone. */
  zoneBoundaryRows: number[];
}

const _arrivalZones: [number, number][] = [[0, 10], [10, 20], [20, 30], [30, 40]];
const _departureZones: [number, number][] = [[0, 8], [8, 18], [18, 28], [28, 36]];

const MODE_CONFIG: Record<string, ModeConfig> = {
  arrival: {
    numCounters: 42,
    zones: _arrivalZones,
    zoneBoundaryRows: _arrivalZones.map(z => z[1]),
  },
  departure: {
    numCounters: 38,
    zones: _departureZones,
    zoneBoundaryRows: _departureZones.map(z => z[1]),
  },
};

/** Returns mode config, defaulting to arrival if mode is unknown/undefined. */
export function getModeConfig(mode?: string): ModeConfig {
  return MODE_CONFIG[mode ?? 'arrival'] ?? MODE_CONFIG['arrival'];
}

/**
 * The 'motor' zone's counter range [start, end) — the dedicated motorcycle
 * counters after the last car zone (e.g. AM41/AM43 for arrival, DM37A/DM37C
 * for departure). Derived from the mode config, never hardcoded to a fixed count.
 */
export function getMotorZoneRange(mode?: string): [number, number] {
  const { zones, numCounters } = getModeConfig(mode);
  const lastCarZoneEnd = zones[zones.length - 1]?.[1] ?? 0;
  return [lastCarZoneEnd, numCounters];
}

/**
 * Format a counter ID into its human-readable display label.
 *
 * Arrival:  C42 → "AM43", C41 → "AM41", C1–C40 → "AC{n}"
 * Departure: C38 → "DM37C", C37 → "DM37A", C1–C36 → "DC{n}"
 */
export function formatCounterLabel(
  counterId: number,
  mode: string | undefined,
): string {
  if (mode === 'arrival') {
    if (counterId === 42) return 'AM43';
    if (counterId === 41) return 'AM41';
    return `AC${counterId}`;
  } else {
    if (counterId === 38) return 'DM37C';
    if (counterId === 37) return 'DM37A';
    return `DC${counterId}`;
  }
}

/**
 * The numbered (non-motor) counters that sit on the driver's side of the
 * booth, 1-based — everything else in that mode's numbered range is
 * passenger-side. Only used to decide the roster grid's y-axis wheel icon
 * (see formatCounterAxisLabel); every other label stays exactly as
 * formatCounterLabel already renders it.
 */
const ARRIVAL_DRIVER_COUNTERS = new Set([
  1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40,
]);
const DEPARTURE_DRIVER_COUNTERS = new Set([
  1, 3, 5, 7, 9, 11, 13, 15, 17, 20, 22, 24, 26, 28, 30, 32, 34, 36,
]);

/** Whether `counterId` (1-based) is on the driver's side of the booth. */
function isDriverSideCounter(counterId: number, mode: string | undefined): boolean {
  return (mode === 'arrival' ? ARRIVAL_DRIVER_COUNTERS : DEPARTURE_DRIVER_COUNTERS).has(counterId);
}

/** Which small glyph (if any) the roster grid draws beside a row's counter number. */
export type CounterAxisIcon = 'wheel' | 'motor';

export interface CounterAxisLabel {
  /** The bare counter number/id text, e.g. "23" or "37A". */
  number: string;
  /** The icon zone glyph, if this row has one. */
  icon?: CounterAxisIcon;
}

/**
 * The roster grid's own y-axis label — deliberately shorter than
 * formatCounterLabel's: the mode's car-counter prefix (AC/DC) moves to the
 * grid's own corner header instead of repeating on every row, so a numbered
 * counter shows bare ("23", not "AC23"). A motor counter drops its whole
 * A/D-M prefix the same way ("AM41" → "41", "DM37A" → "37A") and gets a
 * motorcycle glyph instead of the letter that used to say "motor". Both the
 * number and the icon are returned separately (rather than baked into one
 * string) so RosterGrid can lay them out in fixed-width zones — a number
 * column then an icon column, divided by a hairline — keeping every row's
 * number aligned regardless of whether that row has an icon.
 */
export function formatCounterAxisLabel(counterId: number, mode: string | undefined): CounterAxisLabel {
  const full = formatCounterLabel(counterId, mode);
  const isMotor = full.includes('M');
  if (isMotor) return { number: full.replace(/^[AD]M/, ''), icon: 'motor' };
  if (isDriverSideCounter(counterId, mode)) return { number: `${counterId}`, icon: 'wheel' };
  return { number: `${counterId}` };
}

/** Minutes since midnight for an HHMM string. */
export function hhmmToMinutes(hhmm: string): number {
  return parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(2, 4), 10);
}

/**
 * Length of an HHMM–HHMM range in minutes, counting a range that ends past
 * midnight as running forward into the next day rather than backwards.
 */
export function minutesSpan(start: string, end: string): number {
  const mins = hhmmToMinutes(end) - hhmmToMinutes(start);
  return mins < 0 ? mins + 24 * 60 : mins;
}

/**
 * Convert HHMM string to a slot index (0–NUM_SLOTS-1).
 * Mirrors backend/time_utils.py TimeConverter.hhmm_to_slot().
 * Handles midnight wraparound for night shifts (e.g. startHour=22, time=0030).
 */
export function hmmmToSlot(hhmm: string, startHour = 10): number {
  let h = parseInt(hhmm.slice(0, 2), 10);
  const m = parseInt(hhmm.slice(2), 10);
  // Midnight wraparound: only for night shifts (startHour >= 12), e.g. 0030 after 2200 start
  if (h < startHour && startHour >= 12) h += 24;
  const slot = (h - startHour) * 4 + Math.floor(m / 15);
  return Math.max(0, Math.min(NUM_SLOTS - 1, slot));
}

/**
 * Convert HHMM string to an exclusive end-slot index (0–NUM_SLOTS).
 * Unlike hmmmToSlot, clamps to NUM_SLOTS (not NUM_SLOTS-1) so it works
 * as the exclusive upper bound of a range: for (col = start; col < end; col++).
 * Mirrors backend/time_utils.py TimeConverter.hhmm_to_end_slot().
 */
export function hmmmToEndSlot(hhmm: string, startHour = 10): number {
  let h = parseInt(hhmm.slice(0, 2), 10);
  const m = parseInt(hhmm.slice(2), 10);
  // Midnight wraparound: only for night shifts (startHour >= 12), e.g. 0030 after 2200 start
  if (h < startHour && startHour >= 12) h += 24;
  const slot = (h - startHour) * 4 + Math.floor(m / 15);
  return Math.max(0, Math.min(NUM_SLOTS, slot));
}

/**
 * Validate that a time range is non-empty (start strictly before end) for a given shift.
 * Handles midnight wraparound for night shifts (e.g. "2200"–"0245" is valid).
 * This is the single source of truth for time-range validation on the frontend.
 */
export function isValidTimeRange(start: string, end: string, shiftType: string = 'morning'): boolean {
  if (!start || !end || start.length < 4 || end.length < 4) return false;
  const startHour = getStartHour(shiftType);
  return hmmmToSlot(start, startHour) < hmmmToEndSlot(end, startHour);
}

/**
 * Generate HHMM time slot strings for a shift.
 * slotStep=1 → 15-min intervals (for Swap Tab, DefaultSOSTable start/end dropdowns).
 * slotStep=2 → 30-min intervals (for RORA, HW end time, CounterGrid).
 *
 * This is the single source of truth for time-slot dropdown generation in the frontend.
 * Mirrors the same slot arithmetic as hmmmToSlot (in reverse).
 */
export function generateTimeSlots(shiftType: string, slotStep: 1 | 2 = 1): string[] {
  const startHour = getStartHour(shiftType);
  const slots: string[] = [];
  for (let slot = 0; slot <= NUM_SLOTS; slot += slotStep) {
    const h = startHour + Math.floor(slot / 4);
    const m = (slot % 4) * 15;
    const displayH = h % 24;
    slots.push(`${String(displayH).padStart(2, '0')}${String(m).padStart(2, '0')}`);
  }
  return slots;
}

/** The officer order used everywhere: the default natural order, unless the user has
 *  dragged rows into their own (utils/officerOrder.ts — the single source of truth).
 *  Default: groups by prefix (OT < S < SN, i.e. OT, SOS, Main), then by number.
 *  OT officers (OTn_HHMM-HHMM) are sorted by start time, then end time, then number.
 */
export function naturalSortOfficers(ids: string[]): string[] {
  return applyOfficerOrder(defaultSortOfficers(ids));
}

/** The default order alone (OT, SOS, Main), ignoring any order the user dragged rows into. */
function defaultSortOfficers(ids: string[]): string[] {
  // OT keys: "OT<n>_<startHHMM>-<endHHMM>"
  const reOT = /^OT(\d+)_(\d{4})-(\d{4})$/;
  const re   = /^([A-Za-z]+)(\d+)(.*)$/;

  return [...ids].sort((a, b) => {
    const maOT = reOT.exec(a);
    const mbOT = reOT.exec(b);
    const ma   = re.exec(a);
    const mb   = re.exec(b);

    // Both are OT timing keys → sort by start time, then end time, then number
    if (maOT && mbOT) {
      const startDiff = parseInt(maOT[2], 10) - parseInt(mbOT[2], 10);
      if (startDiff !== 0) return startDiff;
      const endDiff = parseInt(maOT[3], 10) - parseInt(mbOT[3], 10);
      if (endDiff !== 0) return endDiff;
      return parseInt(maOT[1], 10) - parseInt(mbOT[1], 10);
    }

    // Fallback: sort by prefix group, then number
    if (ma && mb) {
      if (ma[1] !== mb[1]) return ma[1].localeCompare(mb[1]);
      return parseInt(ma[2], 10) - parseInt(mb[2], 10);
    }
    return a.localeCompare(b);
  });
}

/** Canvas cell color for an officer, keyed by type metadata or ID prefix fallback. */
/** What the car for an operation mode is called: ACar (arrival) or DCar (departure). */
export function carName(mode: string | undefined): 'ACar' | 'DCar' {
  return mode === 'departure' ? 'DCar' : 'ACar';
}

export function getOfficerColor(officerId: string, meta?: { officer_type?: string; colour_as?: string | null }): string {
  // A Main or SOS officer can be drawn in the other's colour by hand (colour only — see OfficerMetadata.colour_as).
  const own = meta?.officer_type?.toUpperCase();
  const t = own === 'OT' ? own : (meta?.colour_as ?? meta?.officer_type)?.toUpperCase();
  if (t === 'SOS') return '#ffc6d9';
  if (t === 'OT')  return otColorFor(officerId, getOtColorSettings());
  if (t === 'MAIN') return '#a2d2ff';
  if (officerId.startsWith('OT')) return otColorFor(officerId, getOtColorSettings());
  if (officerId.startsWith('S'))  return '#ffc6d9';
  return '#a2d2ff';
}
